<template>
  <div 
    v-if="didaiStore.isPanelOpen" 
    class="didiai-panel"
    :class="{ 'panel-open': didaiStore.isPanelOpen }"
  >
    <!-- Panel Header with Close Button -->
    <div class="panel-header">
      <h2>DiDiAi</h2>
      <div class="header-actions">
        <button @click="didaiStore.closePanel()" class="close-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Panel Content -->
    <div class="panel-content">
      <!-- Sample Questions -->
      <div class="sample-questions" v-if="didaiStore.messages.length <= 1">
        <h3>Try asking:</h3>
        <div class="question-chips">
          <button 
            v-for="question in sampleQuestions" 
            :key="question"
            @click="sendSampleQuestion(question)"
            class="question-chip"
          >
            {{ question }}
          </button>
        </div>
      </div>

      <!-- Chat Messages -->
      <div class="chat-container" ref="chatContainer">
        <div class="messages" v-if="didaiStore.messages.length > 0">
          <div 
            v-for="(message, index) in didaiStore.messages" 
            :key="index"
            :class="['message', message.type]"
          >
            <div class="message-content">
              <!-- User Message -->
              <div v-if="message.type === 'user'" class="message-text" v-html="formatMessage(message.text)"></div>
              
              <!-- AI Message with rich content -->
              <div v-else-if="message.type === 'ai'">
                <!-- Progress -->
                <div v-if="message.progress" class="progress-indicator">
                  <div class="spinner-small"></div>
                  <span>{{ message.progress }}</span>
                </div>
                
                <!-- SQL Code (if live data was queried) -->
                <div v-if="message.sql" class="sql-section">
                  <div class="section-header">
                    <span class="section-icon">📝</span>
                    <span class="section-title">Live Data Query</span>
                    <button @click="copyToClipboard(message.sql)" class="btn-copy">📋 Copy</button>
                  </div>
                  <pre class="sql-code"><code>{{ message.sql }}</code></pre>
                </div>
                
                <!-- Data Table (AG-Grid) -->
                <div v-if="message.table" class="table-section">
                  <div class="section-header">
                    <span class="section-icon">📊</span>
                    <span class="section-title">Data ({{ message.table.rows.length }} rows)</span>
                    <button @click="exportTableToCSV(message.table)" class="btn-copy">📥 CSV</button>
                  </div>
                  <ag-grid-vue
                    v-if="message.table.rows.length > 0"
                    :columnDefs="message.table.columns"
                    :rowData="message.table.rows"
                    :defaultColDef="defaultColDef"
                    class="ag-theme-alpine data-grid"
                    style="height: 300px; width: 100%;"
                  />
                </div>
                
                <!-- Charts (Plotly) -->
                <div v-if="message.charts && message.charts.length > 0" class="charts-section">
                  <div class="section-header">
                    <span class="section-icon">📈</span>
                    <span class="section-title">Visualizations</span>
                  </div>
                  <div 
                    v-for="(chart, chartIndex) in message.charts" 
                    :key="chartIndex"
                    class="chart-container"
                  >
                    <div :ref="el => setChartRef(index, chartIndex, el)" class="plotly-chart"></div>
                  </div>
                </div>
                
                <!-- Main Text Response -->
                <div v-if="message.text" class="message-text" v-html="formatMessage(message.text)"></div>
                
                <!-- Error -->
                <div v-if="message.error" class="error-message">
                  <span class="error-icon">⚠️</span>
                  {{ message.error }}
                </div>
              </div>
              
              <div class="message-time">{{ message.timestamp }}</div>
            </div>
          </div>
        </div>
        
        <!-- Loading indicator -->
        <div v-if="didaiStore.isLoading" class="message ai">
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="chat-input-area">
        <div class="input-container">
          <textarea
            v-model="currentMessage"
            @keydown.enter.prevent="handleKeyPress"
            placeholder="Ask me anything about construction data, projects, costs, or contractors..."
            rows="1"
            class="chat-input"
            :disabled="didaiStore.isLoading"
            ref="textInput"
          ></textarea>
          <button 
            @click="sendMessage"
            :disabled="!currentMessage.trim() || didaiStore.isLoading"
            class="send-button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useDidaiStore } from '@/stores/didiai_store'
import { AgGridVue } from 'ag-grid-vue3'
import Plotly from 'plotly.js-dist-min'
import SPDDataService from '@/services/spdDataService'
import DDADataService from '@/services/ddaDataService'
import axios from 'axios'

const didaiStore = useDidaiStore()
const currentMessage = ref('')
const chatContainer = ref(null)
const textInput = ref(null)
const chartRefs = ref({})

// Mixed sample questions (both types seamlessly integrated)
const sampleQuestions = [
  "Show me the top 10 projects by budget in our system",
  "What's the total of all signed G702s this month?",
  "What are per square foot hard costs for a luxury condo in Miami?",
  "Which contractors have the highest payment amounts?",
  "How many projects have we worked on with Affinius?",
  "Compare payment amounts by contractor for project 709",
  "What is the premium for 'super tall' over 'high rise' towers?"
]

// AG-Grid configuration
const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  flex: 1,
  minWidth: 100
}

const sendSampleQuestion = (question) => {
  currentMessage.value = question
  sendMessage()
}

const setChartRef = (msgIndex, chartIndex, el) => {
  if (el) {
    const key = `chart_${msgIndex}_${chartIndex}`
    chartRefs.value[key] = el
  }
}

const sendMessage = async () => {
  if (!currentMessage.value.trim() || didaiStore.isLoading) return

  const userMessage = {
    type: 'user',
    text: currentMessage.value.trim(),
    timestamp: new Date().toLocaleTimeString()
  }

  didaiStore.addMessage(userMessage)
  const messageText = currentMessage.value
  currentMessage.value = ''
  didaiStore.setLoading(true)

  await scrollToBottom()
  await handleUnifiedQuery(messageText)
}

// UNIFIED QUERY HANDLER - AI decides what data sources to use
const handleUnifiedQuery = async (question) => {
  const aiMessageIndex = didaiStore.messages.length
  const aiMessage = {
    type: 'ai',
    progress: 'Analyzing your question...',
    timestamp: new Date().toLocaleTimeString(),
    charts: []
  }
  didaiStore.addMessage(aiMessage)
  
  try {
    // Step 1: Check if user explicitly wants SPD market data
    const needsMarketData = checkIfNeedsMarketData(question)
    
    // Step 2: For everything else, send to Vanna (let AI decide if it needs to query DB)
    let liveDataResults = null
    let liveDataContext = ''
    
    if (!needsMarketData) {
      updateMessage(aiMessageIndex, { progress: 'Analyzing your question...' })
      liveDataResults = await queryVannaForData(question)
      
      if (liveDataResults && liveDataResults.sql) {
        // Don't show SQL to user - keep internal for debugging
        console.log('[DIDIAI] SQL executed:', liveDataResults.sql)
      }
      
      if (liveDataResults && liveDataResults.tables && liveDataResults.tables.length > 0) {
        // Show the largest table in UI for user
        const largestTable = liveDataResults.tables.reduce((largest, current) => 
          current.rows.length > largest.rows.length ? current : largest
        )
        updateMessage(aiMessageIndex, { table: largestTable })
        
        // Build context from ALL tables for AI
        liveDataContext = buildLiveDataContextFromAllTables(liveDataResults.tables)
      }
      
      if (liveDataResults && liveDataResults.charts) {
        updateMessage(aiMessageIndex, { charts: liveDataResults.charts })
        
        // Render charts
        nextTick(() => {
          liveDataResults.charts.forEach((chart, idx) => {
            const key = `chart_${aiMessageIndex}_${idx}`
            if (chartRefs.value[key]) {
              renderChart(chartRefs.value[key], chart)
            }
          })
        })
      }
    }
    
    // Step 3: Only get SPD data if explicitly requested
    
    let spdResults = { projects: [], buildingTypes: [], marketData: null }
    let ddaResults = { projects: [], contractors: [], drawDetails: [] }
    
    if (needsMarketData) {
      updateMessage(aiMessageIndex, { progress: 'Gathering market data...' })
      ;[spdResults, ddaResults] = await Promise.all([
        SPDDataService.searchData(question).catch(err => {
          console.warn('SPD data unavailable:', err);
          return { projects: [], buildingTypes: [], marketData: null };
        }),
        DDADataService.searchData(question).catch(err => {
          console.warn('DDA data unavailable:', err);
          return { projects: [], contractors: [], drawDetails: [] };
        })
      ])
    } else {
      console.log('[DIDIAI DEBUG] Skipping market data - question is about internal data only')
    }
    
    // Step 4: Build unified context
    let dataContext = buildUnifiedDataContext(spdResults, ddaResults, liveDataContext)
    
    console.log('[DIDIAI DEBUG] liveDataContext length:', liveDataContext?.length || 0)
    console.log('[DIDIAI DEBUG] dataContext length:', dataContext?.length || 0)
    console.log('[DIDIAI DEBUG] dataContext preview:', dataContext?.substring(0, 500))
    
    // Step 5: Check if this is a simple data query or needs AI analysis
    const needsAI = isAnalyticalQuery(question)
    console.log('[DIDIAI DEBUG] Needs AI analysis?', needsAI)
    
    let aiResponse
    if (!needsAI && liveDataResults && liveDataResults.tables?.length > 0) {
      // Simple query - format the data directly without AI
      console.log('[DIDIAI DEBUG] Simple query - formatting data directly')
      updateMessage(aiMessageIndex, { progress: 'Formatting results...' })
      aiResponse = formatSimpleQueryResponse(liveDataResults, question)
    } else {
      // Complex query - use AI for analysis
      console.log('[DIDIAI DEBUG] Analytical query - using AI')
      updateMessage(aiMessageIndex, { progress: 'Generating insights...' })
      const hasLiveData = !!(liveDataResults && (liveDataResults.tables?.length > 0 || liveDataResults.sql))
      const systemPrompt = buildEnhancedSystemPrompt(dataContext, hasLiveData)
      aiResponse = await callOpenAI(question, systemPrompt)
    }
    
    // Step 6: Update with final response
    updateMessage(aiMessageIndex, { 
      text: aiResponse,
      progress: null
    })
    
  } catch (error) {
    console.error('Query error:', error)
    updateMessage(aiMessageIndex, { 
      error: error.message || 'Failed to process query',
      progress: null
    })
  } finally {
    didaiStore.setLoading(false)
    await scrollToBottom()
  }
}

// Check if question explicitly asks for SPD market data
const checkIfNeedsMarketData = (question) => {
  const lowerQ = question.toLowerCase()
  
  // ONLY query SPD if explicitly mentioned or asking about market comparables
  const spdKeywords = ['spd', 'market data', 'market comparable', 'industry data']
  
  const needsMarket = spdKeywords.some(keyword => lowerQ.includes(keyword))
  console.log('[DIDIAI DEBUG] Needs SPD market data?', needsMarket)
  return needsMarket
}

// Check if query needs AI analysis or just data retrieval
const isAnalyticalQuery = (question) => {
  const lowerQ = question.toLowerCase()
  
  // Analytical keywords that require AI
  const analyticalKeywords = [
    'analyze', 'analysis', 'compare', 'comparison', 'trend', 'trending', 'pattern',
    'insight', 'why', 'how does', 'explain', 'summarize', 'summary', 'recommend',
    'suggestion', 'predict', 'forecast', 'optimize', 'improve', 'risk',
    'opportunity', 'should', 'could', 'best', 'worst', 'top', 'bottom',
    'correlation', 'relationship', 'impact', 'effect', 'tell me about'
  ]
  
  return analyticalKeywords.some(keyword => lowerQ.includes(keyword))
}

// Format simple query results without AI - just a brief summary since table is displayed
const formatSimpleQueryResponse = (liveDataResults, question) => {
  if (!liveDataResults || !liveDataResults.tables || liveDataResults.tables.length === 0) {
    return "No data found for your query."
  }
  
  // Find the table with actual data (ignore empty result sets)
  const tablesWithData = liveDataResults.tables.filter(t => t.rows && t.rows.length > 0)
  
  if (tablesWithData.length === 0) {
    return "No matching records found in the database."
  }
  
  // Get the largest/most relevant table
  const mainTable = tablesWithData.reduce((largest, current) => 
    current.rows.length > largest.rows.length ? current : largest
  )
  
  const totalRows = mainTable.rows.length
  
  // Simple, clean response - the table above shows the actual data
  let response = `Found **${totalRows}** record${totalRows !== 1 ? 's' : ''}. `
  response += `The data is displayed in the table above.\n\n`
  
  // Add a brief hint about what columns are available
  if (mainTable.columns && mainTable.columns.length > 0) {
    const keyColumns = mainTable.columns.slice(0, 5).map(c => c.headerName || c.field).join(', ')
    response += `*Showing: ${keyColumns}${mainTable.columns.length > 5 ? ', and more...' : ''}*`
  }
  
  return response
}

// Query Vanna for live data
const queryVannaForData = async (question) => {
  return new Promise((resolve, reject) => {
    const results = {
      sql: null,
      tables: [], // Store ALL tables, not just one
      charts: []
    }
    
    try {
      const token = localStorage.getItem('iP4BLqnibqamRTI_auth_token')
      console.log('[DIDIAI DEBUG] Token exists?', !!token)
      const url = `/api/v1/didiai/vanna_query?question=${encodeURIComponent(question)}&token=${encodeURIComponent(token)}`
      console.log('[DIDIAI DEBUG] Opening EventSource:', url)
      
      const eventSource = new EventSource(url)
      console.log('[DIDIAI DEBUG] EventSource created, initial readyState:', eventSource.readyState)
      
      let eventCount = 0
      const startTime = Date.now()
      
      eventSource.onopen = () => {
        console.log('[DIDIAI DEBUG] EventSource connected successfully')
        console.log('[DIDIAI DEBUG] EventSource readyState:', eventSource.readyState)
      }
      
      eventSource.onmessage = (e) => {
        console.log('[DIDIAI DEBUG] Received GENERIC message event:', e)
        console.log('[DIDIAI DEBUG] Event type:', e.type, 'Data:', e.data)
      }
      
      eventSource.addEventListener('sql', (e) => {
        eventCount++
        console.log(`[DIDIAI DEBUG] Event #${eventCount} - SQL event at ${Date.now() - startTime}ms:`, e.data)
        const data = JSON.parse(e.data)
        results.sql = data.code || data.sql
      })
      
      eventSource.addEventListener('table', (e) => {
        eventCount++
        console.log(`[DIDIAI DEBUG] Event #${eventCount} - TABLE event at ${Date.now() - startTime}ms:`, e.data.substring(0, 100))
        const data = JSON.parse(e.data)
        const columns = data.columns.map(col => ({
          field: col,
          headerName: col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }))
        const newTable = { columns, rows: data.rows || data.data }
        
        // Store ALL tables - AI needs all the context
        results.tables.push(newTable)
        console.log(`[DIDIAI DEBUG] Stored table #${results.tables.length} with ${newTable.rows.length} rows`)
      })
      
      eventSource.addEventListener('chart', (e) => {
        console.log('[DIDIAI DEBUG] Received CHART event:', e.data)
        const chartData = JSON.parse(e.data)
        results.charts.push({
          data: chartData.data,
          layout: enhanceLayout(chartData.layout)
        })
      })
      
      eventSource.addEventListener('done', () => {
        const elapsed = Date.now() - startTime
        eventCount++
        console.log(`[DIDIAI DEBUG] Event #${eventCount} - DONE event at ${elapsed}ms`)
        console.log('[DIDIAI DEBUG] Final results:', {
          sql: !!results.sql,
          tablesCount: results.tables.length,
          chartsCount: results.charts.length,
          totalEvents: eventCount
        })
        eventSource.close()
        resolve(results)
      })
      
      eventSource.onerror = (err) => {
        const elapsed = Date.now() - startTime
        console.error(`[DIDIAI DEBUG] EventSource ERROR after ${elapsed}ms, ${eventCount} events received:`, err)
        console.log('[DIDIAI DEBUG] EventSource readyState:', eventSource.readyState)
        console.log('[DIDIAI DEBUG] Error event details:', {
          type: err.type,
          target: err.target,
          currentTarget: err.currentTarget
        })
        console.log('[DIDIAI DEBUG] Results collected before error:', {
          sql: !!results.sql,
          tablesCount: results.tables.length,
          chartsCount: results.charts.length
        })
        eventSource.close()
        
        // IMPORTANT: Return the results we DID collect, even if stream errored
        console.log('[DIDIAI DEBUG] Resolving with partial results')
        resolve(results)
      }
      
      // Timeout after 30 seconds
      setTimeout(() => {
        const elapsed = Date.now() - startTime
        console.log(`[DIDIAI DEBUG] EventSource TIMEOUT after ${elapsed}ms, ${eventCount} events received`)
        console.log('[DIDIAI DEBUG] Timeout results:', {
          sql: !!results.sql,
          tablesCount: results.tables.length,
          chartsCount: results.charts.length
        })
        eventSource.close()
        resolve(results)
      }, 30000)
      
    } catch (error) {
      console.error('Vanna query error:', error)
      resolve(null)
    }
  })
}

// Build context from live data results with ALL data
const buildLiveDataContext = (table) => {
  if (!table || !table.rows || table.rows.length === 0) return ''
  
  let context = '\n## LIVE DATABASE QUERY RESULTS:\n\n'
  context += `I executed a database query and found **${table.rows.length} records** that directly answer your question.\n\n`
  
  // Show ALL data in a structured way
  context += '### Complete Data:\n'
  table.rows.forEach((row, idx) => {
    context += `\nRecord #${idx + 1}:\n`
    Object.entries(row).forEach(([key, value]) => {
      context += `  - ${key}: ${value}\n`
    })
  })
  
  context += '\n### Summary Statistics:\n'
  context += `- Total Records: ${table.rows.length}\n`
  
  // Calculate totals for numeric fields
  const numericFields = ['original_contract_sum', 'current_payment_due', 'total_records', 'total_contract_sum', 'total_current_payment_due']
  numericFields.forEach(field => {
    const values = table.rows.map(r => parseFloat(r[field])).filter(v => !isNaN(v))
    if (values.length > 0) {
      const total = values.reduce((sum, v) => sum + v, 0)
      context += `- Total ${field}: $${total.toLocaleString()}\n`
    }
  })
  
  context += '\n**IMPORTANT: Use this EXACT data to answer the question. Do NOT say you dont have access.**\n'
  
  return context
}

// Build context from ALL tables returned by Vanna
const buildLiveDataContextFromAllTables = (tables) => {
  if (!tables || tables.length === 0) return ''
  
  let context = '\n## LIVE DATABASE QUERY RESULTS:\n\n'
  context += `I executed ${tables.length} database ${tables.length === 1 ? 'query' : 'queries'} and retrieved comprehensive data.\n\n`
  
  tables.forEach((table, tableIdx) => {
    context += `### Query Result Set #${tableIdx + 1} (${table.rows.length} rows):\n\n`
    
    // Show ALL rows from this table
    table.rows.forEach((row, rowIdx) => {
      context += `Record #${rowIdx + 1}:\n`
      Object.entries(row).forEach(([key, value]) => {
        context += `  - ${key}: ${value}\n`
      })
      context += '\n'
    })
    
    // Calculate totals for numeric fields in this table
    const numericFields = ['original_contract_sum', 'current_payment_due', 'total_records', 'total_contract_sum', 'total_current_payment_due']
    const relevantFields = numericFields.filter(field => 
      table.rows.some(r => r[field] !== undefined && r[field] !== null)
    )
    
    if (relevantFields.length > 0) {
      context += `#### Totals for Query ${tableIdx + 1}:\n`
      relevantFields.forEach(field => {
        const values = table.rows.map(r => parseFloat(r[field])).filter(v => !isNaN(v))
        if (values.length > 0) {
          const total = values.reduce((sum, v) => sum + v, 0)
          context += `- Total ${field}: $${total.toLocaleString()}\n`
        }
      })
      context += '\n'
    }
  })
  
  context += `\n**CRITICAL: All ${tables.length} result sets above contain the EXACT data from the database. Use ALL of this data to provide a comprehensive answer. Do NOT say you don't have access to this data.**\n`
  
  return context
}

// Build unified data context
const buildUnifiedDataContext = (spdResults, ddaResults, liveDataContext) => {
  let context = liveDataContext || ''
  
  // Add SPD market data context (existing logic)
  if (spdResults.queryFilters) {
    context += `\n## MARKET DATABASE FILTERS:\n`
    if (spdResults.queryFilters.locations.length > 0) {
      context += `- Locations: ${spdResults.queryFilters.locations.join(', ')}\n`
    }
    if (spdResults.queryFilters.buildingTypes.length > 0) {
      context += `- Building Types: ${spdResults.queryFilters.buildingTypes.join(', ')}\n`
    }
    if (spdResults.queryFilters.costRange.min || spdResults.queryFilters.costRange.max) {
      context += `- Cost Range: $${spdResults.queryFilters.costRange.min || 0} - $${spdResults.queryFilters.costRange.max || 'unlimited'}\n`
    }
    context += `- Results: ${spdResults.totalCount} matching projects\n\n`
  }
  
  if (spdResults.projects && spdResults.projects.length > 0) {
    context += `\n## MARKET REFERENCE PROJECTS (${spdResults.projects.length} shown):\n`
    spdResults.projects.slice(0, 10).forEach(project => {
      context += `- **${project['Project Name'] || 'Unnamed'}**\n`
      context += `  Location: ${project['City'] || 'Unknown'}, ${project['State'] || 'Unknown'}\n`
      context += `  Type: ${project['Use Type 1'] || project['Asset Category'] || 'Unknown'}\n`
      
      if (project.costs) {
        if (project.costs.hardCostPerGSF) context += `  Hard Cost/SF: $${Math.round(project.costs.hardCostPerGSF)}\n`
        if (project.costs.totalProjectCost) context += `  Total Cost: $${project.costs.totalProjectCost.toLocaleString()}\n`
      }
      if (project.gsf) context += `  GSF: ${project.gsf.toLocaleString()}\n`
      context += '\n'
    })
  }
  
  // Add DDA static data if available
  if (ddaResults.projects && ddaResults.projects.length > 0) {
    context += `\n## YOUR DDA PROJECTS:\n`
    ddaResults.projects.slice(0, 5).forEach(project => {
      context += `- ${project.name || project.project_number}\n`
    })
  }
  
  return context
}

// Enhanced system prompt
const buildEnhancedSystemPrompt = (dataContext, hasLiveData) => {
  const basePrompt = `You are DiDiAi, a specialized construction data analyst with expertise in project costs, schedules, and market trends.

**YOUR CAPABILITIES:**
- Access to live project data from DDA's database (G702s, budgets, payments)
- Access to 50,000+ construction projects from market databases
- Real-time analysis of financial data, contractor performance, and market trends

**YOUR PERSONALITY:**
- Professional yet conversational
- Data-driven but explain insights clearly
- Proactive in identifying trends and anomalies
- Use specific numbers and examples

**RESPONSE STYLE:**
- Lead with the key insight or answer
- Support with specific data points
- Use bullet points for clarity
- Highlight important numbers in **bold**
- Keep responses concise but comprehensive

${hasLiveData ? `
**LIVE DATA AVAILABLE:**
I've already queried your database and retrieved the exact data needed to answer this question. The results are shown above in the data table and charts. Use this live data as the PRIMARY source for your answer.
` : ''}

**DATA AVAILABLE:**
${dataContext}

Now answer the user's question using the data provided. Be specific, cite numbers, and provide actionable insights.`

  return basePrompt
}

const updateMessage = (index, updates) => {
  const msg = didaiStore.messages[index]
  if (msg) {
    Object.assign(msg, updates)
  }
}

const renderChart = (element, chartData) => {
  if (element && chartData) {
    Plotly.newPlot(element, chartData.data, chartData.layout, {
      responsive: true,
      displayModeBar: true,
      displaylogo: false
    })
  }
}

const enhanceLayout = (layout) => {
  return {
    ...layout,
    paper_bgcolor: '#f9fafb',
    plot_bgcolor: '#ffffff',
    font: {
      family: 'Inter, -apple-system, sans-serif',
      color: '#333'
    },
    margin: { t: 40, r: 20, b: 60, l: 60 }
  }
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    alert('Copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const exportTableToCSV = (table) => {
  const headers = table.columns.map(c => c.headerName || c.field).join(',')
  const rows = table.rows.map(row =>
    table.columns.map(c => {
      const val = row[c.field]
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val
    }).join(',')
  ).join('\n')
  
  const csv = `${headers}\n${rows}`
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `didiai_export_${Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const callOpenAI = async (message, systemPrompt) => {
  try {
    const token = localStorage.getItem('iP4BLqnibqamRTI_auth_token')
    const response = await fetch('/api/v1/enhanced_didiai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        message: message,
        systemPrompt: systemPrompt,
        spdResults: {},
        ddaResults: {},
        learnedSuggestions: []
      })
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }

    const data = await response.json()
    return data.response || data.choices?.[0]?.message?.content?.trim() || 'No response'
    
  } catch (error) {
    console.error('OpenAI API Error:', error)
    return 'Service temporarily unavailable. Please try again.'
  }
}

const formatMessage = (text) => {
  if (!text) return ''
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>')
}

const handleKeyPress = (event) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

const scrollToBottom = async () => {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
}

watch(() => didaiStore.isPanelOpen, (isOpen) => {
  if (isOpen) {
    didaiStore.initializeChat()
    nextTick(() => scrollToBottom())
  }
})

onMounted(() => {
  didaiStore.initializeChat()
  
  watch(() => currentMessage.value, () => {
    if (textInput.value) {
      textInput.value.style.height = 'auto'
      textInput.value.style.height = Math.min(textInput.value.scrollHeight, 120) + 'px'
    }
  })
})
</script>

<style scoped lang="scss">
// Keep ALL existing styles from original DiDiAiPanel.vue

.didiai-panel {
  position: fixed;
  top: 60px;
  right: 0;
  width: 480px;
  height: calc(100vh - 60px);
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  border-left: 1px solid #e5e7eb;
  z-index: 50;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
}

.didiai-panel.panel-open {
  transform: translateX(0);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #2c3e50;
  
  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: white;
    font-weight: 700;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, #46D8D5, #4E6BE3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: white;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #46D8D5;
    transform: scale(1.1);
  }
}

.panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
}

.sample-questions {
  margin-bottom: 16px;
  
  h3 {
    color: #555;
    margin-bottom: 12px;
    font-size: 0.95rem;
  }
}

.question-chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.question-chip {
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  color: #0ea5e9;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.75rem;
  text-align: left;
  transition: all 0.2s;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  width: 100%;
  white-space: normal;
  
  &:hover {
    background: #0ea5e9;
    color: white;
    transform: translateX(4px);
  }
}

.chat-container {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message {
  display: flex;
  
  &.user {
    justify-content: flex-end;
    
    .message-content {
      background: #0ea5e9;
      color: white;
      max-width: 85%;
    }
  }
  
  &.ai {
    justify-content: flex-start;
    
    .message-content {
      background: white;
      color: #333;
      border: 1px solid #e5e7eb;
      max-width: 95%;
    }
  }
}

.message-content {
  padding: 12px 16px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-text {
  line-height: 1.5;
  font-size: 0.9rem;
  
  :deep(strong) {
    font-weight: 600;
    color: #0ea5e9;
  }
  
  :deep(em) {
    font-style: italic;
  }
}

.message-time {
  font-size: 0.7rem;
  opacity: 0.6;
  margin-top: 6px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.section-icon {
  font-size: 16px;
}

.section-title {
  flex: 1;
  font-weight: 600;
  font-size: 13px;
  color: #555;
}

.btn-copy {
  padding: 4px 8px;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  color: #0ea5e9;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #0ea5e9;
    color: white;
  }
}

.sql-section {
  margin: 12px 0;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
}

.sql-code {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.4;
  max-height: 200px;
}

.table-section {
  margin: 12px 0;
}

.data-grid {
  border-radius: 4px;
  font-size: 12px;
}

.charts-section {
  margin: 12px 0;
}

.chart-container {
  margin: 10px 0;
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.plotly-chart {
  width: 100%;
  height: 300px;
}

.progress-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0ea5e9;
  font-style: italic;
  margin-bottom: 8px;
  font-size: 13px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid #0ea5e9;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid #f44336;
  border-radius: 6px;
  color: #d32f2f;
  font-size: 13px;
}

.error-icon {
  font-size: 18px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
  
  span {
    width: 6px;
    height: 6px;
    background: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

.chat-input-area {
  border-top: 1px solid #e5e7eb;
  padding: 20px 0;
  background: #2c3e50;
  margin: 0 -20px;
  padding-left: 20px;
  padding-right: 20px;
}

.input-container {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 16px;
}

.chat-input {
  flex: 1;
  padding: 16px 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  resize: none;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  max-height: 120px;
  min-height: 52px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &::placeholder {
    color: #9ca3af;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-color: #46D8D5;
    box-shadow: 0 0 0 3px rgba(70, 216, 213, 0.3);
  }
  
  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
  }
}

.send-button {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
  width: 56px;
  height: 56px;
  border-radius: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
